import React, { useEffect } from 'react';
import { Styles } from '@t2c/shared-fe';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { CustomDialogContainer, CustomDialogTitle } from '../../Common/dialog';
import { CircularProgress } from '@mui/material';
import { Box } from '@mui/material';
import { AIMedia, AIMediaStatus, Status, WebSocketDataReturnType } from '../../@types';
import { useRouter } from 'next/router';
import { useAppContext } from '../../hooks';
import CustomizedProgressBars from '../../Common/ProgressBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AI_MEDIA_GENERATE_ERR_MSGS } from '../../utils';
import { Iconify } from '@sdf/base';
import { ProgressStuckNote } from '../../Common';

interface Props {
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
  data: AIMedia;
}

export const BrollClipProccessingModal: React.FC<Props> = ({
  shouldOpenDialog,
  handleCloseDialog,
  data,
}) => {
  const { push } = useRouter();

  const {
    websocket: { aiGenerateTaskMap, handleRemoveFromTaskMap },
  } = useAppContext();

  const taskData = aiGenerateTaskMap.get(data?.id);

  const cardProgress = taskData?.progress || 0;
  const isTaskSuccessful = taskData?.status === Status.Success;
  const isInQueue =
    !taskData || taskData?.status === Status.InQueue || taskData?.status === Status.NotStarted;
  const isTaskInProgress = taskData?.status === Status.InProgress;
  const isTaskFailed = taskData?.status === Status.Error || data?.status === AIMediaStatus.Error;
  const failedErrorMsg = taskData?.data?.errorMessage;

  const onCloseDialog = () => {
    handleCloseDialog();
    if (isTaskSuccessful || isTaskFailed) {
      handleRemoveFromTaskMap({ id: data?.id, type: WebSocketDataReturnType.AI_GENERATE });
    }
  };

  useEffect(() => {
    if (isTaskSuccessful) {
      onCloseDialog();
      push(`broll/${data?.id}`);
    }
  }, [isTaskSuccessful]);

  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth="sm">
      <CustomDialogTitle sx={styles.title} title={'Generating Clip'} onClose={onCloseDialog} />
      <DialogContent dividers sx={styles.dialogContent}>
        <>
          {(isTaskInProgress || isInQueue) && (
            <Stack sx={styles.loading}>
              <Typography fontWeight={600} fontSize={'26px'} textAlign={'center'}>
                {isInQueue
                  ? 'Your video is in queue, getting closer to the spotlight!'
                  : 'Generating your AI Video Clip...'}
              </Typography>
              <CircularProgress />
              {!isInQueue && <CustomizedProgressBars value={cardProgress} sx={{ width: '80%' }} />}

              <Typography textAlign={'center'} color={(theme) => theme.palette.text.placeholder}>
                Please wait while we create stunning visuals for your project.
              </Typography>
              <ProgressStuckNote />
            </Stack>
          )}

          {isTaskFailed && (
            <>
              <Typography sx={styles.heading} textAlign={'center'}>
                Oops!
              </Typography>
              <ErrorOutlineIcon sx={styles.errorIcon} color="error" />
              <Typography sx={styles.errorMessage}>
                {AI_MEDIA_GENERATE_ERR_MSGS.includes(failedErrorMsg)
                  ? failedErrorMsg
                  : `We encountered an issue! Your broll clip couldn’t be created. Please try again.`}
              </Typography>
            </>
          )}
        </>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: 2 }}>
        {(isInQueue || isTaskInProgress) && (
          <Button
            variant="contained"
            sx={{
              color: (theme) => theme.palette.text.primary,
              width: '200px',
              boxShadow: 'none',
            }}
            onClick={onCloseDialog}
          >
            Minimize
          </Button>
        )}
        {isTaskFailed && (
          <Button
            variant="outlined"
            sx={{
              width: '200px',
              color: (theme) => theme.palette.text.primary,
            }}
            onClick={onCloseDialog}
          >
            Close
          </Button>
        )}
      </Box>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: 500,
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: 4,
    gap: 2,
  },
  heading: { fontSize: '26px', fontWeight: 600, textAlign: 'center' },
  errorIcon: { width: 75, height: 75 },
  errorMessage: { color: (theme) => theme.palette.text.error, textAlign: 'center' },
};
